import React, { useMemo, useState } from 'react';
import { Heart, Flame, Trophy, Star, Send, Users, Award, Lock, X, ChevronRight } from 'lucide-react';
import { todayStr } from '../lib/dates.js';

export function StatsPage({ theme, profile, partner, pair, pairStats, completions }) {
  const today = todayStr();
  const [expandedFilter, setExpandedFilter] = useState(null);

  // Compute personal stats from completions
  const myStats = useMemo(() => {
    const mine = completions.filter(c => c.completedBy === profile?.id);
    const todayCount = mine.filter(c => c.completedAt?.startsWith(today)).length;
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - 7);
    const weekStartStr = weekStart.toISOString().split('T')[0];
    const weekCount = mine.filter(c => c.completedAt?.split('T')[0] >= weekStartStr).length;
    const totalPoints = mine.reduce((sum, c) => sum + (c.pointsEarned || 0), 0);

    const sentReceived = mine.filter(c => c.taskList === 'sent').length;
    const sharedDone = mine.filter(c => c.taskList === 'shared').length;
    const privateDone = mine.filter(c => c.taskList === 'private').length;

    return {
      todayCount,
      weekCount,
      totalCompletions: mine.length,
      totalPoints,
      sentReceived,
      sharedDone,
      privateDone,
      mine,  // expose for filter modal
      weekStartStr,
    };
  }, [completions, profile, today]);

  // Filter completions based on selected category
  const filteredCompletions = useMemo(() => {
    if (!expandedFilter) return [];
    const all = myStats.mine;
    switch (expandedFilter.type) {
      case 'today':
        return all.filter(c => c.completedAt?.startsWith(today));
      case 'week':
        return all.filter(c => c.completedAt?.split('T')[0] >= myStats.weekStartStr);
      case 'sent':
        return all.filter(c => c.taskList === 'sent');
      case 'shared':
        return all.filter(c => c.taskList === 'shared');
      case 'private':
        return all.filter(c => c.taskList === 'private');
      default:
        return [];
    }
  }, [expandedFilter, myStats, today]);

  const achievements = useMemo(() => {
    return [
      {
        id: 'first_send',
        name: 'Første sending',
        desc: 'Send din første opgave',
        emoji: '✉️',
        unlocked: !!partner && completions.some(c => c.completedBy !== profile?.id && c.taskList === 'sent'),
        premium: false,
      },
      {
        id: 'week_streak_3',
        name: '3 ugers streak',
        desc: 'Hold streaken i 3 uger',
        emoji: '🔥',
        unlocked: (pairStats?.streakDays || 0) >= 21,
        premium: false,
      },
      {
        id: 'week_streak_8',
        name: '8 ugers streak',
        desc: 'Hold streaken i 8 uger',
        emoji: '⚡',
        unlocked: (pairStats?.streakDays || 0) >= 56,
        premium: false,
      },
      {
        id: 'love_master',
        name: 'Hjertets master',
        desc: 'Nå 100 kærlighedspoint',
        emoji: '💕',
        unlocked: (pairStats?.loveScore || 0) >= 100,
        premium: false,
      },
      {
        id: 'perfect_week',
        name: 'Perfekt uge',
        desc: 'Løs alle deadlines i én uge',
        emoji: '🏆',
        unlocked: false,
        premium: true,
      },
      {
        id: 'game_master',
        name: 'Game-master',
        desc: 'Premium achievement-system',
        emoji: '🎮',
        unlocked: false,
        premium: true,
      },
    ];
  }, [partner, completions, profile, pairStats]);

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  return (
    <div>
      <h1 style={{ margin: '0 0 16px', fontSize: 22, fontWeight: 700 }}>Stats</h1>

      {/* Hero stats */}
      {pair ? (
        <div style={{
          background: `linear-gradient(135deg, ${theme.coral}20, ${theme.gold}15)`,
          border: `1px solid ${theme.coral}30`,
          borderRadius: 14,
          padding: 18,
          marginBottom: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Heart size={16} color={theme.coral} fill={theme.coral} />
            <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: theme.coral }}>
              Jeres team
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <HeroStat theme={theme} value={pairStats?.loveScore || 0} label="Kærlighedspoint" color={theme.coral} />
            <HeroStat theme={theme} value={pairStats?.streakDays || 0} label="Dage i streak" color={theme.gold} />
            <HeroStat theme={theme} value={pairStats?.totalCompletions || 0} label="Total løst" color={theme.teal} />
          </div>
        </div>
      ) : (
        <div style={{
          background: theme.bgCard,
          border: `1px solid ${theme.borderSubtle}`,
          borderRadius: 14,
          padding: 18,
          marginBottom: 18,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Du er solo</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
            Når du parres med din partner, ser du jeres fælles statistikker her.
          </p>
        </div>
      )}

      {/* Tile row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 18 }}>
        <Tile theme={theme} icon={Award} color={theme.coral}
          value={myStats.totalCompletions} label="Opgaver løst" />
        <Tile theme={theme} icon={Trophy} color={theme.gold}
          value={myStats.totalPoints} label="Optjente point" />
      </div>

      {/* Personal breakdown — clickable rows */}
      <Section theme={theme} title="Mit overblik">
        <ClickableRow theme={theme} icon={Star} label="Løst i dag" value={myStats.todayCount} color={theme.gold}
          onClick={() => setExpandedFilter({ type: 'today', label: 'Løst i dag' })} />
        <ClickableRow theme={theme} icon={Star} label="Løst i denne uge" value={myStats.weekCount} color={theme.gold}
          onClick={() => setExpandedFilter({ type: 'week', label: 'Løst i denne uge' })} />
        {partner && (
          <>
            <ClickableRow theme={theme} icon={Send} label={`Opgaver løst fra ${partner.name?.split(' ')[0]}`} value={myStats.sentReceived} color={theme.coral}
              onClick={() => setExpandedFilter({ type: 'sent', label: `Opgaver løst fra ${partner.name?.split(' ')[0]}` })} />
            <ClickableRow theme={theme} icon={Users} label="Fælles opgaver løst" value={myStats.sharedDone} color={theme.teal}
              onClick={() => setExpandedFilter({ type: 'shared', label: 'Fælles opgaver løst' })} />
          </>
        )}
        <ClickableRow theme={theme} icon={Star} label="Private opgaver løst" value={myStats.privateDone} color={theme.textMuted}
          onClick={() => setExpandedFilter({ type: 'private', label: 'Private opgaver løst' })} />
      </Section>

      {/* Achievements */}
      <Section theme={theme} title={`Achievements (${unlockedCount}/${achievements.length})`}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, padding: 4 }}>
          {achievements.map(a => (
            <div key={a.id} style={{
              padding: '14px 12px',
              background: a.unlocked ? `${theme.gold}15` : theme.bgCard,
              border: `1px solid ${a.unlocked ? `${theme.gold}50` : theme.borderSubtle}`,
              borderRadius: 12,
              opacity: a.unlocked ? 1 : 0.55,
              position: 'relative',
            }}>
              {a.premium && !a.unlocked && (
                <div style={{
                  position: 'absolute',
                  top: 6, right: 6,
                  background: theme.gold,
                  color: 'black',
                  fontSize: 8,
                  fontWeight: 700,
                  padding: '2px 6px',
                  borderRadius: 999,
                }}>PREMIUM</div>
              )}
              <div style={{ fontSize: 24, marginBottom: 6 }}>
                {a.unlocked ? a.emoji : <Lock size={18} color={theme.textDim} />}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: a.unlocked ? theme.gold : theme.textMuted }}>
                {a.name}
              </div>
              <div style={{ fontSize: 10, color: theme.textDim, marginTop: 2 }}>
                {a.desc}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Modal: completion details when row clicked */}
      {expandedFilter && (
        <CompletionListModal
          theme={theme}
          title={expandedFilter.label}
          completions={filteredCompletions}
          onClose={() => setExpandedFilter(null)}
        />
      )}
    </div>
  );
}

function CompletionListModal({ theme, title, completions, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 200,
    }} onClick={onClose}>
      <div style={{
        background: theme.bgElevated,
        borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 560,
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        animation: 'tt-slide-up 0.3s ease-out',
      }} onClick={e => e.stopPropagation()}>
        <style>{`@keyframes tt-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: theme.textMuted,
            cursor: 'pointer', padding: 4,
          }}><X size={18} /></button>
        </div>

        <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
          {completions.length === 0 ? (
            <div style={{ textAlign: 'center', color: theme.textMuted, padding: '30px 20px', fontSize: 13 }}>
              Ingen opgaver i denne kategori endnu
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {completions.map(c => (
                <div key={c.id} style={{
                  padding: '12px 14px',
                  background: theme.bgCard,
                  border: `1px solid ${theme.borderSubtle}`,
                  borderRadius: 10,
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}>
                    <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: theme.text }}>
                      {c.taskTitle}
                    </div>
                    {c.pointsEarned > 0 && (
                      <div style={{
                        fontSize: 11,
                        color: theme.gold,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}>+{c.pointsEarned}</div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: theme.textMuted }}>
                    {formatCompletionDate(c.completedAt)}
                    {c.taskList === 'sent' && ' · fra partner'}
                    {c.taskList === 'shared' && ' · fælles'}
                    {c.taskList === 'private' && ' · privat'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatCompletionDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86400000);

  if (d.toDateString() === today.toDateString()) {
    return `I dag kl. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (d.toDateString() === yesterday.toDateString()) {
    return `I går kl. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return d.toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function HeroStat({ theme, value, label, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Tile({ theme, icon: Icon, color, value, label }) {
  return (
    <div style={{
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 12,
      padding: '14px 10px',
      textAlign: 'center',
    }}>
      <Icon size={16} color={color} style={{ marginBottom: 6 }} />
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color }}>
        {value}
      </div>
      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Section({ theme, title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{
        margin: '0 0 8px', fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 1,
        color: theme.textMuted,
      }}>{title}</h3>
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.borderSubtle}`,
        borderRadius: 12,
      }}>
        {children}
      </div>
    </div>
  );
}

function ClickableRow({ theme, icon: Icon, label, value, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '12px 14px',
      background: 'transparent',
      border: 'none',
      borderBottom: `1px solid ${theme.borderSubtle}`,
      cursor: 'pointer',
      color: theme.text,
      textAlign: 'left',
    }}>
      <Icon size={14} color={color || theme.textMuted} />
      <div style={{ flex: 1, fontSize: 13, color: theme.text }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || theme.text }}>{value}</div>
      <ChevronRight size={14} color={theme.textDim} />
    </button>
  );
}
